import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { NAVIGATION, SOCIAL_LINKS } from '../../../constants';
import { Icon } from '../../common';

// SVG Icons for social media
const InstagramIcon = () => (
  <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
  </svg>
);

const YouTubeIcon = () => (
  <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
  </svg>
);

/**
 * Header component with navigation
 * Appears as floating header over hero section
 */
const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogoClick = () => {
    const scrollContainer = document.querySelector('.overflow-y-auto');
    if (scrollContainer) scrollContainer.scrollTo(0, 0);
    navigate('/');
  };

  const handleNavClick = (href) => {
    setMenuOpen(false);
    if (href.startsWith('#')) {
      const element = document.querySelector(href);
      element?.scrollIntoView({ behavior: 'smooth' });
    } else {
      const scrollContainer = document.querySelector('.overflow-y-auto');
      if (scrollContainer) scrollContainer.scrollTo(0, 0);
      navigate(href);
    }
  };

  return (
    <>
      <header className="absolute top-0 left-0 right-0 z-50 flex items-center justify-between px-6 py-6 bg-gradient-to-b from-black/60 to-transparent pointer-events-none">
        {/* Logo */}
        <button 
          onClick={handleLogoClick}
          className="flex items-end gap-1 pointer-events-auto cursor-pointer bg-transparent border-none"
        >
          <span className="text-lg font-black tracking-tight text-white drop-shadow-md leading-none">
            Matisa Is Live
          </span>
        </button>

        {/* Menu Button */}
        <button 
          onClick={() => setMenuOpen(true)}
          className="p-2 text-white hover:bg-white/10 rounded-full transition-colors pointer-events-auto"
          aria-label="Open menu"
        >
          <Icon name="menu" size="md" />
        </button>
      </header>

      {/* Mobile Menu Overlay */}
      {menuOpen && (
        <div className="fixed inset-0 z-[100] bg-background-darker/95 backdrop-blur-lg flex flex-col items-center justify-center gap-8 animate-fade-in">
          {/* Close Button */}
          <button
            onClick={() => setMenuOpen(false)}
            className="absolute top-6 right-6 p-2 text-white hover:bg-white/10 rounded-full transition-colors"
            aria-label="Close menu"
          >
            <Icon name="close" size="lg" />
          </button>

          {/* Logo in menu */}
          <button 
            onClick={() => handleNavClick('/')}
            className="text-3xl font-black tracking-tight text-white mb-4"
          >
            MATISA
          </button>

          <nav className="flex flex-col items-center gap-6">
            {NAVIGATION.map((item) => (
              <button
                key={item.label}
                onClick={() => handleNavClick(item.href)}
                className={`text-2xl font-bold transition-colors ${
                  item.label === 'Book a Show'
                    ? 'text-gold drop-shadow-[0_0_8px_rgba(212,175,55,0.6)] hover:drop-shadow-[0_0_12px_rgba(212,175,55,0.8)]'
                    : location.pathname === item.href 
                      ? 'text-primary' 
                      : 'text-white hover:text-primary'
                }`}
              >
                {item.label}
              </button>
            ))}
          </nav>
          
          {/* Social Links in Menu */}
          <div className="flex gap-4 mt-8">
            <a 
              href={SOCIAL_LINKS.instagram}
              target="_blank" 
              rel="noopener noreferrer"
              className="p-3 rounded-full bg-white/10 hover:bg-gradient-to-br hover:from-purple-500 hover:to-pink-500 transition-all text-white"
              aria-label="Instagram"
            >
              <InstagramIcon />
            </a>
            <a 
              href={SOCIAL_LINKS.youtube}
              target="_blank" 
              rel="noopener noreferrer"
              className="p-3 rounded-full bg-white/10 hover:bg-red-600 transition-colors text-white"
              aria-label="YouTube"
            >
              <YouTubeIcon />
            </a>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
